import { useEffect, useState } from 'react';
import { Bounce, toast } from 'react-toastify';
import { useFieldArray, useFormContext, useFormState } from "react-hook-form";
import { schemaUtils } from '@centivo-business/utils';
import { useAccountFormStore } from "../../../store/accountFormStore";
import { SUB_ACCOUNT_DRAFT } from "../../../utils/constants";

const step = 4;
const SubAccountStepFour = ({ activeStep, onSaveSubAccountSucceeded }) => {
    const { control, getValues, trigger } = useFormContext();
    const { errors } = useFormState({ control });
    const { append, update } = useFieldArray({
        control,
        name: "subAccounts",
    });

    const { accounts } = useAccountFormStore();
    const [isValid, setIsValid] = useState(undefined);

    const draft = getValues(SUB_ACCOUNT_DRAFT);

    const defaultValidationFields = [
        `${SUB_ACCOUNT_DRAFT}.parentAccount`,
        `${SUB_ACCOUNT_DRAFT}.accountName`,
        `${SUB_ACCOUNT_DRAFT}.accountHccId`,
        `${SUB_ACCOUNT_DRAFT}.accountType`,
        `${SUB_ACCOUNT_DRAFT}.primaryAddress1`,
        `${SUB_ACCOUNT_DRAFT}.primaryAddressCity`,
        `${SUB_ACCOUNT_DRAFT}.primaryAddressState`,
        `${SUB_ACCOUNT_DRAFT}.primaryAddressZip`,
    ];

    const billingValidationFields = [
        `${SUB_ACCOUNT_DRAFT}.billingCycle`,
        `${SUB_ACCOUNT_DRAFT}.billingAddress1`,
        `${SUB_ACCOUNT_DRAFT}.billingAddressCity`,
        `${SUB_ACCOUNT_DRAFT}.billingAddressState`,
        `${SUB_ACCOUNT_DRAFT}.billingAddressZip`
    ];

    const validationFields = draft?.billParent
        ? defaultValidationFields
        : [...defaultValidationFields, ...billingValidationFields];

    useEffect(() => {
        const triggerFormValidation = async () => {
            const result = await trigger(validationFields);
            setIsValid(result);
        };

        triggerFormValidation();
    }, [activeStep]);

    if (activeStep !== step) return null;

    const getDateString = (date) => {
        if (date) {
            if (typeof date === 'string') {
                return date.substring(0, 10);
            } else if (date instanceof Date) {
                return date.toISOString().substring(0, 10);
            }
        }
        return '';
    };

    const onSaveSubAccount = () => {        
        const subAccountDraft = getValues(SUB_ACCOUNT_DRAFT);

        const allAccountIdentifiers = accounts.reduce((acc, account) => {
            const topAccountIds = [account.accountName, account.accountHccId];
            const subAccountIds = account.subAccounts?.flatMap(sa => [sa.accountName, sa.accountHccId]) ?? [];
            return [...acc, ...topAccountIds, ...subAccountIds];
        }, []);

        if (allAccountIdentifiers.some(id => id === subAccountDraft.accountName)) {
            toast.error('Account Name already exists', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
                transition: Bounce,
            });
            return;
        }

        if (allAccountIdentifiers.some(id => id === subAccountDraft.accountHccId)) {
            toast.error('Account HCC ID already exists', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
                transition: Bounce,
            });
            return;
        }

        if (subAccountDraft.accountId) {
            // If accountId exists, the sub account has already been created, which means we are editing
            const subAccounts = getValues("subAccounts");
            subAccounts.forEach((subAccount, index) => {
                if (subAccount.accountId === subAccountDraft.accountId) {
                    update(index, subAccountDraft);
                } else if (subAccount.parentAccount.key === subAccountDraft.accountId) {
                    const newParentAccount = {
                        ...subAccount.parentAccount,
                        value: subAccountDraft.accountHccId,
                        label: `${subAccountDraft.accountName} (${subAccountDraft.accountHccId})`
                    };
                    update(index, { ...subAccount, parentAccount: newParentAccount });
                }
            });
        } else {
            const accountId = crypto.randomUUID();
            append({ accountId, ...subAccountDraft });
        }

        onSaveSubAccountSucceeded();
    };

    const topAccount = getValues();
    const draftErrors = errors[SUB_ACCOUNT_DRAFT] ?? {};

    return (
        <div className={'container'} style={{ padding: '1rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div>
                    <h4>As Of Date</h4>
                    <p>{getDateString(topAccount.asOfDate)}</p>
                </div>
                <div>
                    <h4>Update All Versions</h4>
                    <p>{topAccount.updateAllVersions ? 'YES' : 'NO'}</p>
                </div>
                <div>
                    <h4>Transaction Type</h4>
                    <p>{topAccount.action.toUpperCase()}</p>
                </div>
                <div>
                    <h4>Parent Account</h4>
                    <p>{draft.parentAccount?.label ?? ''}</p>
                    {draftErrors.parentAccount && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{draftErrors.parentAccount.message}</p>}
                </div>
                <div>
                    <h4>Account Name</h4>
                    <p>{draft.accountName ?? ''}</p>
                    {draftErrors.accountName && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{draftErrors.accountName.message}</p>}
                </div>
                <div>
                    <h4>Account HCC ID</h4>
                    <p>{draft.accountHccId ?? ''}</p>
                    {draftErrors.accountHccId && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{draftErrors.accountHccId.message}</p>}
                </div>
                <div>
                    <h4>Tax Entity</h4>
                    <p>{topAccount.taxEntity?.label ?? ''}</p>
                </div>
                <div>
                    <h4>Account Type</h4>
                    <p>{draft.accountType?.label ?? ''}</p>
                    {draftErrors.accountType && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{draftErrors.accountType.message}</p>}
                </div>
                <div>
                    <h4>Effective Date</h4>
                    <p>{topAccount.effectiveDate ?? ''}</p>
                </div>
                <div>
                    <h4>Subscriptions Allowed</h4>
                    <p>{draft.subscriptionsAllowed ? 'YES' : 'NO'}</p>
                </div>
                <div>
                    <h4>Primary Correspondence Address</h4>
                    {draft.primaryAddress1 && draft.primaryAddressCity && draft.primaryAddressState && draft.primaryAddressZip && (
                        <>
                            <p>{draft.primaryAddress1}</p>
                            {draft.primaryAddress2 && <p>{draft.primaryAddress2}</p>}
                            {draft.primaryAddress3 && <p>{draft.primaryAddress3}</p>}
                            <p>{`${draft.primaryAddressCity}, ${draft.primaryAddressState.value} ${draft.primaryAddressZip}`}</p>
                        </>
                    )}
                    {draftErrors.primaryAddress1 && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{draftErrors.primaryAddress1.message}</p>}
                    {draftErrors.primaryAddressCity && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{draftErrors.primaryAddressCity.message}</p>}
                    {draftErrors.primaryAddressState && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{draftErrors.primaryAddressState.message}</p>}
                    {draftErrors.primaryAddressZip && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{draftErrors.primaryAddressZip.message}</p>}
                    <br />
                    <p>{`Ignore Address Check: ${draft.ignorePrimaryAddressCheck ? 'YES' : 'NO'}`}</p>
                </div>
                <div>
                    <h4>Bill Parent</h4>
                    <p>{draft.billParent ? 'YES' : 'NO'}</p>
                </div>
                {!draft.billParent && (
                    <>
                        <div>
                            <h4>Billing Cycle Name</h4>
                            <p>{draft.billingCycle?.label ?? ''}</p>
                            {draftErrors.billingCycle && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{draftErrors.billingCycle.message}</p>}
                        </div>
                        <div>
                            <h4>Premium Billing Address</h4>
                            {draft.billingAddress1 && draft.billingAddressCity && draft.billingAddressState && draft.billingAddressZip && (
                                <>
                                    <p>{draft.billingAddress1}</p>
                                    {draft.billingAddress2 && <p>{draft.billingAddress2}</p>}
                                    {draft.billingAddress3 && <p>{draft.billingAddress3}</p>}
                                    <p>{`${draft.billingAddressCity}, ${draft.billingAddressState.value} ${draft.billingAddressZip}`}</p>
                                </>
                            )}
                            {draftErrors.billingAddress1 && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{draftErrors.billingAddress1.message}</p>}
                            {draftErrors.billingAddressCity && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{draftErrors.billingAddressCity.message}</p>}
                            {draftErrors.billingAddressState && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{draftErrors.billingAddressState.message}</p>}
                            {draftErrors.billingAddressZip && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{draftErrors.billingAddressZip.message}</p>}
                            <br />
                            <p>{`Ignore Address Check: ${draft.ignoreBillingAddressCheck ? 'YES' : 'NO'}`}</p>
                        </div>
                    </>
                )}
            </div>
            <div style={{ display: 'flex', gap: '1rem', padding: '2rem 1rem 1rem 0rem' }}>
                <button type="button" className="btn edit-btn" disabled={!isValid || !schemaUtils.doesDraftHaveAnyInput(draft)} onClick={onSaveSubAccount}>
                    Save Sub Account
                </button>
            </div>
            <p className="sub-title">*Note: this action will NOT create a sub account in HRP. It just saves it locally under the top account.</p>
            <br />
        </div>
    );
};

export default SubAccountStepFour;
